import { defineStore } from 'pinia';
import router from 'src/app/router/router';
import store from 'commons/store/Store';
import { useIdentityStore } from 'commons/stores/identity';

type PartnerType = 'SHOP' | 'MP' | 'BOTH';

/** The fields of a `user/shops` entry the onboarding checklist reads. */
type OnboardingShop = {
  id: string;
  setupWizardDone: boolean;
  hidden: boolean;
};

export type OnboardingStepKey =
  | 'CREATE_PRODUCT'
  | 'CREATE_PRODUCT_MP'
  | 'CREATE_PRODUCT_SHOP'
  | 'SETUP_SHOWROOM'
  | 'SETUP_SHOP'
  | 'LAUNCH_SHOP';

export type OnboardingStep = {
  key: OnboardingStepKey;
  name: string;
  done: boolean | null | undefined;
  onClick: () => void;
  scope: PartnerType[];
  condition?: () => boolean;
};

export type OnboardingStepsForScope = {
  scope: PartnerType;
  steps: OnboardingStep[] | null;
};

type State = {
  initialized: boolean;
};

const partnerType = (): PartnerType => store.getters['user/partnerType'];

const userShops = (): OnboardingShop[] => store.state.user.shops;

export const useOnboardingStore = defineStore('onboarding', {
  state: (): State => ({
    initialized: false,
  }),

  getters: {
    /** `null` until `ideas/checkIdeaIntents` has resolved. */
    ideaPublished: (): boolean | null =>
      store.state.ideas.hasAtLeastOneIdeaWithIntent,
    mpProductPublished(): boolean | null | undefined {
      if (store.getters['settings/newPublishingJourneyAvailabilityMp']) {
        return store.getters['publishingCore/hasMpIntent'];
      }

      if (store.getters['settings/oldPublishingJourneyAvailabilityMp']) {
        return this.ideaPublished;
      }

      return undefined;
    },
    shopProductPublished(): boolean | null | undefined {
      if (store.getters['settings/newPublishingJourneyAvailabilityShop']) {
        return store.getters['publishingCore/hasShopIntent'];
      }

      if (store.getters['settings/oldPublishingJourneyAvailabilityShop']) {
        return this.ideaPublished;
      }

      return undefined;
    },
    atLeastOneShopConfigured: (): boolean =>
      userShops().some((shop) => shop.setupWizardDone),
    atLeastOneShopLaunched: (): boolean =>
      userShops().some((shop) => !shop.hidden),
    firstNotConfiguredShop: (): OnboardingShop | undefined =>
      userShops().find((shop) => !shop.setupWizardDone),
    firstOfflineShop: (): OnboardingShop | undefined =>
      userShops().find((shop) => shop.hidden),
    showroomConfigured: (): boolean => useIdentityStore().usernameSetup,
    steps(state): OnboardingStep[] | null {
      if (!state.initialized) {
        return null;
      }

      return [
        {
          key: 'CREATE_PRODUCT',
          name: 'ONBOARD.CHECKLIST.STEP.CREATE_PRODUCT',
          done: this.ideaPublished,
          onClick: () => router.push({ name: 'partnerarea.ideas' }),
          scope: ['SHOP', 'MP', 'BOTH'],
          condition: () =>
            store.getters['settings/oldPublishingJourneyAvailabilityBoth'],
        },
        {
          key: 'CREATE_PRODUCT_MP',
          name: 'ONBOARD.CHECKLIST.STEP.CREATE_PRODUCT_MP',
          done: this.mpProductPublished,
          onClick: () =>
            store.getters['settings/newPublishingJourneyAvailabilityMp']
              ? router.push({ name: 'partnerarea.mp.publishing' })
              : router.push({ name: 'partnerarea.ideas' }),
          scope: ['MP', 'BOTH'],
          condition: () =>
            !store.getters['settings/oldPublishingJourneyAvailabilityBoth'],
        },
        {
          key: 'CREATE_PRODUCT_SHOP',
          name: 'ONBOARD.CHECKLIST.STEP.CREATE_PRODUCT_SHOP',
          done: this.shopProductPublished,
          onClick: () =>
            store.getters['settings/newPublishingJourneyAvailabilityShop']
              ? router.push({ name: 'partnerarea.shop.publishing' })
              : router.push({ name: 'partnerarea.ideas' }),
          scope: ['SHOP', 'BOTH'],
          condition: () =>
            !store.getters['settings/oldPublishingJourneyAvailabilityBoth'],
        },
        {
          key: 'SETUP_SHOWROOM',
          name: 'ONBOARD.CHECKLIST.STEP.SETUP_SHOWROOM',
          done: this.showroomConfigured,
          onClick: () =>
            router.push({
              name: 'partnerarea.showroom',
              query: {
                editProfile: 'true',
              },
            }),
          scope: ['MP', 'BOTH'],
        },
        {
          key: 'SETUP_SHOP',
          name: 'ONBOARD.CHECKLIST.STEP.SETUP_SHOP',
          done: this.atLeastOneShopConfigured,
          onClick: () => {
            // Also undone when the partner has no shops, so there may be none to open.
            if (this.firstNotConfiguredShop) {
              router.push({
                name: 'shop-area',
                params: {
                  shopId: this.firstNotConfiguredShop.id,
                },
              });
            }
          },
          scope: ['SHOP', 'BOTH'],
        },
        {
          key: 'LAUNCH_SHOP',
          name: 'ONBOARD.CHECKLIST.STEP.LAUNCH_SHOP',
          done: this.atLeastOneShopLaunched,
          onClick: () => {
            // Also undone when the partner has no shops, so there may be none to open.
            if (this.firstOfflineShop) {
              router.push({
                name: 'shop-area.general.shop-status',
                params: {
                  shopId: this.firstOfflineShop.id,
                },
              });
            }
          },
          scope: ['SHOP', 'BOTH'],
        },
      ];
    },
    relevantSteps(): OnboardingStep[] | null {
      return this.steps
        ? this.steps.filter(
            (step) =>
              step.scope.includes(partnerType()) &&
              (!step.condition || step.condition()),
          )
        : null;
    },
    finishedRelevantSteps(): OnboardingStepsForScope {
      return {
        scope: partnerType(),
        steps: this.relevantSteps
          ? this.relevantSteps.filter((step) => step.done)
          : null,
      };
    },
    unfinishedRelevantSteps(): OnboardingStepsForScope {
      return {
        scope: partnerType(),
        steps: this.relevantSteps
          ? this.relevantSteps.filter((step) => !step.done)
          : null,
      };
    },
    firstUnfinishedRelevantStep(): OnboardingStep | undefined | null {
      return this.relevantSteps
        ? this.relevantSteps.find((step) => !step.done)
        : null;
    },
    finished(): boolean {
      return (
        !this.unfinishedRelevantSteps.steps ||
        !this.unfinishedRelevantSteps.steps.length
      );
    },
  },

  actions: {
    initialize() {
      this.initialized = true;
    },
  },
});
